import { z } from "zod";
import type { RtiKind } from "./training.js";

/**
 * The hours ledger as the API returns it (REC-01, CNT-26; design.md
 * §14.5). Append-only: a correction is a reversal of a whole entry, then
 * the replacement, each a row of its own that names the row it corrects —
 * so the list is the history, and the balance is the sum.
 */

/** `completion` — banked by a confirmed completion (CNT-26); `correction` — a reversal or a replacement. */
export type HoursSource = "completion" | "correction";

export interface HoursEntry {
  id: string;
  /** When the row was written. */
  occurredAt: string;
  /** The day the hours were earned (YYYY-MM-DD, the tenant's calendar). */
  entryDate: string;
  /** Signed: a reversal is negative. */
  hours: number;
  kind: RtiKind;
  source: HoursSource;
  programId: string;
  person: { id: string; displayName: string };
  curriculumEntryId: string;
  /** The completion that banked it, and the assignment that completion finished. */
  externalCompletionId?: string;
  assignmentId?: string;
  correctsEntryId?: string;
  /** Why a correction was made, in the corrector's words. */
  reason?: string;
  submittedBy?: { id: string; displayName: string };
}

export interface HoursListResponse {
  entries: HoursEntry[];
  /** True when there were more rows than one page holds. */
  truncated: boolean;
}

/**
 * A correction (REC-01: "corrections are new entries"): the whole entry is
 * reversed, and when `hours` is given a replacement follows at that figure.
 * No partial reversal — "16 became 12" is −16 then +12, so the history
 * reads the way an ATR would reconstruct it.
 */
export const CorrectHoursSchema = z.object({
  reason: z.string().trim().min(3).max(500),
  /** Two places, as the column holds it (numeric(6,2)): 0.001 is refused here rather than rounded to nothing there. */
  hours: z.number().min(0.01).max(9999.99).multipleOf(0.01).optional(),
});
export type CorrectHoursRequest = z.infer<typeof CorrectHoursSchema>;

export interface CorrectHoursResponse {
  /** Every ledger row of that completion after the correction: the entry, its reversal and any replacement. */
  entries: HoursEntry[];
}
